import React from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';
import { waLink, waLinkProps } from '../mock';
import { trackWhatsAppClick } from '../lib/analytics';
import { useCart } from '../context/CartContext';
import { parsePriceMXN, formatMXN, getShippingCost, FREE_SHIPPING_THRESHOLD_MXN } from '../lib/utils';
import SiteHeader from './SiteHeader';
import AboutContact from './AboutContact';
import CheckoutButton from './CheckoutButton';
import WhatsAppIcon from './WhatsAppIcon';

const CartPage = ({ onOpenProduct }) => {
  const { items, removeItem } = useCart();
  const subtotal = items.reduce((sum, i) => sum + parsePriceMXN(i.price) * i.qty, 0);
  const shipping = items.length > 0 ? getShippingCost(subtotal) : 0;
  const total = subtotal + shipping;

  const buildWaMessage = () => {
    const lines = items
      .map((i) => `- ${i.name}${i.size ? ` (${i.size})` : ''} x${i.qty}`)
      .join('\n');
    const shippingLine = shipping > 0 ? `Envío: $${formatMXN(shipping)} MXN` : 'Envío: GRATIS';
    return `Hola QUINLATE, quiero pedir:\n${lines}\n\n${shippingLine}\nTotal: $${formatMXN(total)} MXN`;
  };

  return (
    <>
      <SiteHeader onOpenProduct={onOpenProduct} />

      <section className="q-section" data-testid="cart-section">
        <div className="q-section__inner">
          <div className="q-section__head">
            <span className="q-section__eyebrow">Tu pedido</span>
            <h2 className="q-section__title">Carrito</h2>
            <p className="q-section__sub">
              {items.length === 0
                ? 'Aquí solo aparece lo que tú guardes — todavía no has agregado nada.'
                : `Tienes ${items.length} ${items.length === 1 ? 'pieza guardada' : 'piezas guardadas'}.`}
            </p>
          </div>

          {items.length === 0 ? (
            <div className="q-empty" data-testid="cart-empty-state">
              <p className="q-empty__title">Tu carrito está vacío</p>
              <p className="q-empty__text">Explora una colección y agrega tus piezas favoritas.</p>
              <div className="q-cart__empty-links">
                <Link to="/joyeria" className="q-btn q-btn--gold q-btn--sm" data-testid="cart-empty-joyeria-link">
                  Ver Joyería
                </Link>
                <Link to="/gorras" className="q-btn q-btn--ghost q-btn--sm" data-testid="cart-empty-gorras-link">
                  Ver Gorras
                </Link>
              </div>
            </div>
          ) : (
            <div className="q-cart">
              <ul className="q-cart__list">
                {items.map((i) => (
                  <li key={i.cartId} className="q-cart__item" data-testid={`cart-item-${i.cartId}`}>
                    <div className="q-cart__item-info">
                      <span className="q-cart__item-name">{i.name}{i.size ? ` (${i.size})` : ''}</span>
                      <span className="q-cart__item-meta">Cantidad: {i.qty} · {i.price}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeItem(i.cartId)}
                      className="q-cart__item-remove"
                      aria-label={`Quitar ${i.name}`}
                      data-testid={`cart-remove-${i.cartId}`}
                    >
                      <X size={16} strokeWidth={1.5} />
                    </button>
                  </li>
                ))}
              </ul>

              <div className="q-cart__total q-cart__total--sub">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">${formatMXN(subtotal)} MXN</span>
              </div>
              <div className="q-cart__total q-cart__total--sub">
                <span>Envío</span>
                <span data-testid="cart-shipping">
                  {shipping > 0 ? `$${formatMXN(shipping)} MXN` : 'GRATIS'}
                </span>
              </div>
              <div className="q-cart__total">
                <span>Total</span>
                <strong data-testid="cart-total">${formatMXN(total)} MXN</strong>
              </div>
              {shipping > 0 && (
                <p className="q-cart__shipping-hint" data-testid="cart-shipping-hint">
                  Te faltan ${formatMXN(FREE_SHIPPING_THRESHOLD_MXN - subtotal)} MXN para envío gratis.
                </p>
              )}

              <div className="q-cart__actions">
                <a
                  href={waLink(buildWaMessage())}
                  {...waLinkProps}
                  className="q-btn q-btn--ghost q-btn--wa"
                  data-testid="cart-whatsapp-btn"
                  onClick={() => trackWhatsAppClick('cart_page', { items: items.length })}
                >
                  <WhatsAppIcon size={18} /> Pedir por WhatsApp
                </a>
                <CheckoutButton items={items} shipping={shipping} />
                <Link to="/joyeria" className="q-cart__continue" data-testid="cart-continue-shopping">
                  + Seguir agregando piezas
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <AboutContact />
    </>
  );
};

export default CartPage;
